"use client"

import * as React from "react"
import * as TogglePrimitive from "@radix-ui/react-toggle"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@shared/utils"

/**
 * Variants for the Toggle component styling using class-variance-authority.
 */
const toggleVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-10 px-3",
        sm: "h-8 px-2",
        lg: "h-11 px-5",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    }
  }
);

/**
 * Toggle component - a styled toggle button built on Radix UI TogglePrimitive.
 *
 * @remarks
 * Supports variant and size styling variants.
 * Forwards ref to the underlying Radix TogglePrimitive Root.
 *
 * @param className - Additional CSS classes to apply.
 * @param variant - Styling variant, either "default" or "outline".
 * @param size - Size variant: "default", "sm", or "lg".
 * @param props - Other native TogglePrimitive.Root props.
 * @param ref - Ref forwarded to Radix TogglePrimitive.Root.
 *
 * @returns A React component for toggle button.
 *
 * @example
 * ```tsx
 * <Toggle variant="outline" size="sm" pressed={isOn} onPressedChange={setIsOn}>
 *   Toggle me
 * </Toggle>
 * ```
 */
const Toggle = React.forwardRef<
  React.ComponentRef<typeof TogglePrimitive.Root>,
  React.ComponentProps<typeof TogglePrimitive.Root> &
  VariantProps<typeof toggleVariants>
>(({ className, variant, size, ...props }, ref) => (
  <TogglePrimitive.Root
    ref={ref}
    className={cn(toggleVariants({ variant, size, className }))}
    {...props}
  />
));

Toggle.displayName = TogglePrimitive.Root.displayName;

export { Toggle, toggleVariants };
